import React, { useCallback, useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Alert, Card, Flex, Spin } from 'antd'
import { useFetchConfidenceDistributionAnalyticsQuery } from '../../../../../../../store/apis/analytics.api'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'
import { buildAssignmentPath } from '../../../../../../../utils/link.helper'
import {
    ConfidenceLevels,
    getConfidenceLevelColor,
    getConfidenceLevelNoun,
} from '../../../../../../../constants/confidence-levels'
import { useNavigate } from 'react-router-dom'
import { useThemeColors } from '../../../../../../../hooks/useThemeColors'
import useRubricContext from '../../../../../../../hooks/useRubricContext'

const BAR_HEIGHT = 32

const ConfidenceDistributionChart = ({ token }) => {
    const navigate = useNavigate()
    const colors = useThemeColors()

    const {
        data: analytics,
        isFetching: isFetchingAnalytics,
        error,
    } = useFetchConfidenceDistributionAnalyticsQuery({ token })

    const { questions, isFetching: isFetchingQuestions } = useRubricContext()

    const isFetching = isFetchingAnalytics || isFetchingQuestions

    const data = useMemo(() => {
        if (!analytics?.questionRubrics || !questions) return []

        return analytics.questionRubrics
            .map(analyticsQr => {
                // Find the enriched question to get names
                const question = questions.find(q => q.id === analyticsQr.questionId)
                if (!question) return null

                const questionRubric = question.questionRubrics?.find(qr => qr.id === analyticsQr.id)
                if (!questionRubric) return null

                const displayName = questionRubric.rubric?.name 
                    ? `${question.name} - ${questionRubric.rubric.name}`
                    : question.name

                const result = {
                    name: displayName,
                    questionRubricId: analyticsQr.id,
                    totalSubmissions: analyticsQr.totalSubmissionsPerQuestionRubric,
                }

                // Add data for each confidence level
                analyticsQr.confidenceLevels.forEach(level => {
                    result[`confidence_${level.level}`] = level.percentFromQuestionSubmissions
                    result[`confidence_${level.level}_count`] = level.numberOfSubmissions
                })

                return result
            })
            .filter(Boolean)
    }, [analytics, questions])

    const chartHeight = Math.max(160, (data?.length || 0) * BAR_HEIGHT + 100)

    const handleBarClick = data => {
        if (data && data.questionRubricId) navigate(buildAssignmentPath(token, data.questionRubricId))
    }

    const renderCustomYAxisTick = useCallback(props => {
        const { x, y, payload } = props
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={0} textAnchor='end' fill={colors.secondaryText} style={{ fontSize: 12 }}>
                    {payload.value.length > 20 ? `${payload.value.substring(0, 20)}...` : payload.value}
                </text>
            </g>
        )
    }, [colors.secondaryText])

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null

        const questionRubricData = data.find(d => d.name === label)
        if (!questionRubricData) return null

        const confidenceLevels = payload.map(entry => ({
            level: parseInt(entry.name.split('_')[1]),
            value: entry.value,
            count: questionRubricData[`${entry.name}_count`],
            color: getConfidenceLevelColor(parseInt(entry.name.split('_')[1])),
        }))

        return (
            <Card size='small' className='min-w-[300px]'>
                <Title level={5} style={{ margin: '0 0 8px 0', fontSize: '0.9rem' }}>
                    {label}
                </Title>
                <Flex vertical gap={8}>
                    {confidenceLevels.map((item, index) => (
                        <Flex key={index} justify='space-between' align='center' gap={8}>
                            <Text style={{ color: getConfidenceLevelColor(item.level) }}>
                                {getConfidenceLevelNoun(item.level)}
                            </Text>
                            <Text>{`${item.value}% - ${item.count} submissions`}</Text>
                        </Flex>
                    ))}
                    <Text type='secondary'>Total Submissions: {questionRubricData.totalSubmissions}</Text>
                </Flex>
            </Card>
        )
    }

    if (error) return <Alert message={error} type='error' showIcon />

    return (
        <Spin spinning={isFetching} tip='Loading...'>
            <ResponsiveContainer width='100%' height={chartHeight}>
                <BarChart data={data} layout='vertical' margin={{ top: 0, right: 12, left: 12, bottom: 36 }}>
                    <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                    <XAxis
                        type='number'
                        tickFormatter={value => `${value}%`}
                        label={{
                            value: 'Percent of Submissions',
                            position: 'bottom',
                            offset: 15,
                        }}
                        domain={[0, 100]}
                    />
                    <YAxis
                        dataKey='name'
                        type='category'
                        tick={renderCustomYAxisTick}
                        width={144}
                        label={{
                            value: 'Rubrics',
                            angle: -90,
                            position: 'left',
                        }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {Object.values(ConfidenceLevels).map(level => (
                        <Bar
                            key={`confidence_${level}`}
                            dataKey={`confidence_${level}`}
                            stackId='confidence'
                            fill={getConfidenceLevelColor(level)}
                            name={`confidence_${level}`}
                            onClick={handleBarClick}
                            style={{ cursor: 'pointer' }}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </Spin>
    )
}

export default ConfidenceDistributionChart
